import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../GRI300/GRI308/SectionHeading";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const GRI409 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    riskOperationsForcedLabor: {
      riskOperations: "",
      typeOfOperation: "",
      countriesOrGeographic: "",
    },

    forcedLaborActions: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);
        const data = response.data.data.GRI400?.forcedOrCompulsory || {};
        setFormData({
          riskOperationsForcedLabor: {
            riskOperations:
              data.riskOperationsForcedLabor?.riskOperations || "",
            countriesOrGeographic:
              data.riskOperationsForcedLabor?.countriesOrGeographic || "",
            typeOfOperation:
              data.riskOperationsForcedLabor?.typeOfOperation || "",
          },
          forcedLaborActions: data.forcedLaborActions || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData
    if (!formData.riskOperationsForcedLabor.riskOperations) {
      newErrors.riskOperations = "This field is required";
      formIsValid = false;
    }

    if (!formData.riskOperationsForcedLabor.typeOfOperation) {
      newErrors.typeOfOperation = "This field is required";
      formIsValid = false;
    }

    if (!formData.riskOperationsForcedLabor.countriesOrGeographic) {
      newErrors.countriesOrGeographic = "This field is required";
      formIsValid = false;
    }

    if (!formData.forcedLaborActions) {
      newErrors.forcedLaborActions = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;  // Validate form before submission
      const payload = {
        type: "GRI400",
        submitted409: true,
        forcedOrCompulsory: {
          ...formData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/410-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/410-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      <p className={styles.section_heading} style={{ marginBottom: hide ? "-1rem" : "0" }}>
        409 : Forced or Compulsory Labour
      </p>
      <p className={styles.main_heading} style={{ marginBottom: hide ? "-1rem" : "0" }}>
        409-1 : Operations and suppliers at significant risk for incidents of
        forced or compulsory labour
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}

        <SectionHeading text="a. Identify operations and suppliers where there is a significant risk of incidents involving forced or compulsory labour? Please specify:" />
        <CustomTextField
          value={formData.riskOperationsForcedLabor.riskOperations}
          onChange={(e) =>
            handleNestedInputChange(
              "riskOperationsForcedLabor",
              "riskOperations",
              e.target.value
            )
          }
          error={errors.riskOperations}
          helperText={errors.riskOperations}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i. The type of operation (e.g., manufacturing plant) and supplier." />
          <CustomTextField
            value={formData.riskOperationsForcedLabor.typeOfOperation}
            onChange={(e) =>
              handleNestedInputChange(
                "riskOperationsForcedLabor",
                "typeOfOperation",
                e.target.value
              )
            }
            error={errors.typeOfOperation}
            helperText={errors.typeOfOperation}
            disabled={disabled}
          />

          <SectionHeading text="ii. Countries or geographic areas where operations and suppliers are considered at risk." />
          <CustomTextField
            value={formData.riskOperationsForcedLabor.countriesOrGeographic}
            onChange={(e) =>
              handleNestedInputChange(
                "riskOperationsForcedLabor",
                "countriesOrGeographic",
                e.target.value
              )
            }
            error={errors.countriesOrGeographic}
            helperText={errors.countriesOrGeographic}
            disabled={disabled}
          />
        </Box>

        <SectionHeading text="b. What actions did the organization take during the reporting period to contribute to the elimination of all forms of forced or compulsory labour?" />
        <CustomTextField
          value={formData.forcedLaborActions}
          onChange={(e) =>
            handleInputChange("forcedLaborActions", e.target.value)
          }
          error={errors.forcedLaborActions}
          helperText={errors.forcedLaborActions}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI409;
